import { Button, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../lib/apiClient";
import { tenantKey } from "../../lib/tenantQuery";

/**
 * MÜVEKKİL ALANI KUTUSU (CLOS-1123 · 1147). `ContractCollaborationSection`'dan çıkarıldı;
 * aynı yerde çizilir. Sorumluluğu tek: sözleşmenin bağlı olduğu müvekkilin alanlarına
 * paylaşımı açmak / geri çekmek ve açık paylaşımın künyesini göstermek.
 *
 * ⚠ 1128 (genel sözleşme detayı yerleşimi) kapsamına GİRİLMEDİ: kutu yer değiştirmedi,
 * yalnız kendi dosyasına taşındı ki 1128 onu bağımsız konumlandırabilsin.
 */
export function ContractClientAreaBox({
  contractId,
  tenantId,
  yonetebilir,
  baglamAnahtari,
}: {
  contractId: string;
  tenantId: string;
  yonetebilir: boolean;
  /** Paylaşım bağlamı sorgusunun anahtarı — açma/geri çekme sonrası birlikte tazelenir. */
  baglamAnahtari: readonly unknown[];
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [onayBekleyen, setOnayBekleyen] = useState<string | null>(null);
  const alanAnahtari = tenantKey(tenantId, "contract", contractId, "muvekkil-alanlari");
  const alanHedefleri = useQuery({
    enabled: yonetebilir,
    queryKey: alanAnahtari,
    queryFn: async () =>
      (
        await api<
          {
            workspaceTenantId: string;
            clientId: string;
            clientName: string;
            workspaceName: string;
            open: boolean;
            share: {
              level: string;
              grantedByName: string | null;
              startsAt: string;
              endsAt: string | null;
            } | null;
            /** CLOS-1123 / Halit S3: aktif hizmet ilişkisi yoksa yeni paylaşım açılamaz. */
            eligible: boolean;
            blockedReason: "no_active_service_relationship" | null;
          }[]
        >(`/api/contracts/${contractId}/client-area-shares`, { tenantId })
      ).data,
  });

  const alanaAc = useMutation({
    mutationFn: async (workspaceTenantId: string) =>
      api(`/api/contracts/${contractId}/client-area-shares`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({ workspaceTenantId }),
      }),
    onSuccess: async () => {
      /**
       * ⚠ İKİ ANAHTAR DA TAZELENİYOR (canlı tur bulgusu): alana açmak HEM hedef listesini
       * (`open` bayrağı) HEM paylaşım bağlamını (`clientAreas`) değiştiriyor. Yalnız
       * birini tazelemek, ekranın yarısını bayat bırakıyordu — kullanıcı açtığı alanı
       * bağlamda göremiyordu.
       */
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: alanAnahtari }),
        queryClient.invalidateQueries({ queryKey: baglamAnahtari }),
      ]);
      toast.success("Sözleşme Müvekkil Alanına açıldı");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Müvekkil Alanına açılamadı"),
  });

  const alanGeriCek = useMutation({
    mutationFn: async (workspaceTenantId: string) =>
      api(`/api/contracts/${contractId}/client-area-shares/revoke`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({ workspaceTenantId }),
      }),
    onSuccess: async () => {
      /** ⚠ Geri çekme de iki yüzeyi birden değiştiriyor; ikisi de tazeleniyor. */
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: alanAnahtari }),
        queryClient.invalidateQueries({ queryKey: baglamAnahtari }),
      ]);
      toast.success("Müvekkil Alanı erişimi geri çekildi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Erişim geri çekilemedi"),
  });

  return (
    <>
      {/*
          ⚠ ÜÇ HEDEF TÜRÜ AYRI (FAZ 0B): İç organizasyon üyesi (kayıt erişimi penceresi),
          MÜVEKKİL ALANI (bu bölüm) ve haricî katılımcı (aşağıdaki bölüm). Talimat bu
          üçünün karıştırılmamasını şart koşuyor: müvekkil temsilcisini "haricî katılımcı"
          olarak davet etmek çözüm DEĞİL — temsilcilik ve haricî katılım ayrı erişim
          kaynakları.

          ⚠ BÖLÜM ARTIK HEDEF YOKKEN DE ÇİZİLİYOR — ve bunu bir saha raporu zorladı.
          Önceki sürümde hedef yoksa bölüm HİÇ görünmüyordu; gerekçesi "boş bir kutu 'alan
          yok' der, oysa doğru cümle 'bu sözleşme için alan hedefi yok'" idi. Gerekçe doğru,
          sonucu yanlıştı: sessiz boşluk, ekranı okuyan kişiye "böyle bir özellik yok" diye
          okundu ve 4 Eylül 2026 tarihli bir bug raporu tam olarak bunu "akış eksik" diye
          bildirdi. Oysa akış uçtan uca vardı.

          Doğru cümle zaten biliniyordu; eksik olan onu YAZMAKTI.

          ⚠ İKİ DAL: hedef VARSA liste, hedef YOKSA sebep. İlk yazımda dış koşulu
          `yonetebilir` yapıp hedef kontrolünü düşürmüştüm — o zaman ikinci dal hiç
          erişilemez kalıyor ve boş durum asla çizilmiyordu. Bekçi yakaladı.
        */}
      {yonetebilir && (alanHedefleri.data ?? []).length > 0 ? (
        <section className="flex flex-col gap-2">
          <h3 className="text-xs font-medium text-fg-strong">Müvekkil Alanı</h3>
          <p className="text-xs text-fg-soft">
            Sözleşmeyi müvekkilin alanına açtığınızda, alanın aktif temsilcileri kendi yetkileri
            kapsamında sözleşmeyi görür. Büronun özel notları, görevleri ve iç yorumları açılmaz.
          </p>
          {/*
              ⚠ TEK EKSİK CÜMLE: paylaşımın OKUMA olduğu. Ekran zaten kapsamı anlatıyordu
              ama neden "Paylaşım düzeyi: Görüntüleme" yazdığını söylemiyordu; aynı bug
              raporu bunu "temsilcinin geniş yetkisiyle tutarsız" diye bildirdi. Tutarsızlık
              yok — efektif erişim bir KESİŞİM ve alan paylaşımı tavanı okumada tutuyor
              (`clientAreaShare.ts`: müvekkil tarafı kanonik metni değiştiremez, yazma ayrı
              bir ürün kararı). Cümle o tavanı söylüyor.
            */}
          <p className="text-xs text-fg-soft">
            Alana açmak sözleşmeyi görünür kılar; kanonik metni değiştirme yetkisi vermez.
          </p>
          <ul className="flex flex-col">
            {(alanHedefleri.data ?? []).map((hedef) => (
              <li
                key={hedef.workspaceTenantId}
                className="flex flex-wrap items-center justify-between gap-2 border-b border-border-default py-1.5 last:border-0"
              >
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-sm text-fg">
                    {hedef.clientName} — Müvekkil Alanı
                  </span>
                  <span className="text-xs text-fg-soft">
                    {hedef.open ? "Müvekkil Alanına açık" : "Henüz açılmadı"}
                  </span>
                  {/*
                      ⚠ KÜNYE, DURUM DEĞİL (bulgu raporu §10): ekran yalnız "açık" diyordu.
                      "Erişimi geri çek" kararını veren kişi neyi geri çektiğini —
                      kapsamı, kimin açtığını, ne zamandan beri açık olduğunu ve bir
                      bitişi olup olmadığını — göremiyordu.

                      ⚠ SÜRESİZ ERİŞİMDE TARİH YAZILMIYOR: "bitiş yok" ile "bitiş
                      bilinmiyor" aynı şey değil ve boş bir tarih ikisini karıştırırdı.
                    */}
                  {hedef.share ? (
                    <span
                      className="text-xs text-fg-soft"
                      data-testid={`alan-kunye-${hedef.workspaceTenantId}`}
                    >
                      {/*
                          ⚠ SABİT "Görüntüleme" (CLOS-1106 · ADR-0063). Müvekkil Alanı
                          paylaşımı OKUMA tavanında: `clientAreaShare.ts` kayıt erişimini
                          `level: "read"` ile açıyor ve `CLIENT_WORKSPACE_KEYS`'te yazma
                          anahtarı yok — sunucu BU satır için `write` ÜRETMİYOR. Eski
                          `level === "write" ? "Düzenleme" : "Görüntüleme"` dalı hiç
                          çalışmayan bir yetki beyanıydı; ADR bunu "kabul edilen borç" diye
                          yazmıştı, denetim planı (5 Eylül 2026) dalı kapattı. Kullanıcının
                          KENDİ düzeyi (`viewer.level`, yukarıdaki "Sizin erişiminiz") iki
                          değeri de gerçekten taşır ve dallı KALIR.
                        */}
                      Görüntüleme
                      {hedef.share.grantedByName ? ` · ${hedef.share.grantedByName} açtı` : ""}
                      {` · ${new Date(hedef.share.startsAt).toLocaleDateString("tr-TR")}`}
                      {hedef.share.endsAt
                        ? ` · ${new Date(hedef.share.endsAt).toLocaleDateString("tr-TR")} tarihine kadar`
                        : " · süresiz"}
                    </span>
                  ) : null}
                </span>
                {hedef.open ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    loading={alanGeriCek.isPending}
                    onClick={() => alanGeriCek.mutate(hedef.workspaceTenantId)}
                  >
                    Erişimi geri çek
                  </Button>
                ) : onayBekleyen === hedef.workspaceTenantId ? (
                  /*
                      ⚠ ONAY ADIMI (talimat şartı): *"İşlem ekranında en az ... Onay/
                      confirmation"*. Alana açmak, sözleşmeyi başka bir organizasyonun
                      bütün aktif temsilcilerine görünür kılıyor — tek tıkla dönülemez bir
                      görünürlük kararı. Onay ekranı ne olacağını AÇIKÇA söylüyor.
                    */
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-fg">
                      {hedef.clientName} alanının aktif temsilcileri bu sözleşmeyi kendi yetkileri
                      kapsamında görecek. Onaylıyor musunuz?
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      loading={alanaAc.isPending}
                      onClick={() => {
                        setOnayBekleyen(null);
                        alanaAc.mutate(hedef.workspaceTenantId);
                      }}
                    >
                      Onayla ve aç
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setOnayBekleyen(null)}
                    >
                      Vazgeç
                    </Button>
                  </span>
                ) : (
                  /*
                      ⚠ AKTİF İLİŞKİ YOKSA AÇILMAZ (CLOS-1123 / Halit S3): sunucu 409 döner;
                      düğmeyi kapatıp SEBEBİ yazmak, tıklayıp hata görmekten iyi. Açık paylaşım
                      hâlâ geri çekilebilir — yukarıdaki dal ilişkiden bağımsız.
                    */
                  <span className="flex flex-col items-end gap-1">
                    <Button
                      type="button"
                      size="sm"
                      disabled={!hedef.eligible}
                      onClick={() => setOnayBekleyen(hedef.workspaceTenantId)}
                    >
                      Müvekkil Alanına Aç
                    </Button>
                    {!hedef.eligible ? (
                      <span
                        className="text-xs text-fg-soft"
                        data-testid={`alan-engel-${hedef.workspaceTenantId}`}
                      >
                        Aktif hizmet ilişkisi yok; müvekkil daveti kabul edince açılabilir.
                      </span>
                    ) : null}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : yonetebilir ? (
        <section className="flex flex-col gap-2">
          <h3 className="text-xs font-medium text-fg-strong">Müvekkil Alanı</h3>
          {/*
              ⚠ NEDEN hedef olmadığını söylüyor, "kayıt yok" demiyor. İki ayrı sebep var
              (sözleşme bir müvekkile bağlı değil, ya da müvekkilin aktif alanı yok) ve
              ikisi de kullanıcının düzeltebileceği şeyler — ama hangisi olduğunu sunucu
              söylemiyor, o yüzden ikisi birden yazılıyor.

              ⚠ BURAYA AKSİYON KONMADI ve bu bilinçli: müvekkil bağlama ayrı bir yüzey ve
              ayrı bir yetki (`client.*`). Buradan bir düğme koymak, yetkisi olmayan
              kullanıcıyı 403'e götüren bir yol açardı. Açıklayıcı boş durum güvenli olan;
              yönlendirme, yetki tasarımı yapıldıktan sonra eklenir.
            */}
          <p className="text-xs text-fg-soft">
            Bu sözleşme için açılabilecek bir Müvekkil Alanı yok: sözleşme bir müvekkile bağlı değil
            ya da bağlı olduğu müvekkilin aktif bir alanı bulunmuyor.
          </p>
        </section>
      ) : null}
    </>
  );
}
